import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { Card } from 'src/components/common/Card';
import Cooladata from 'src/components/common/Cooladata';
import { Socials } from 'src/components/common/Header/Socials';
import Icon from 'src/components/common/Icon';
import Preloader from 'src/components/common/Preloader';
import { ProgressLine } from 'src/components/common/ProgressLine';
import StorycardsCredits from 'src/components/common/StorycardsCredits';
import { ROOT } from 'src/constants/routes';
import { useAppSelector, useResponsive } from 'src/hooks';
import { AppDispatch } from 'src/store';
import { restoreResults } from 'src/store/data';
import { sendResults } from 'src/store/data/actions';
import { selectLoadingResults, selectParties, selectResults } from 'src/store/data/selectors';
import { Party, PartyResult } from 'src/types/data-types';
import { analytics } from 'src/utils/analytics';
import { clientSession, isQuizResults } from 'src/utils/session-storage';

import AnimatedPercent from './AnimatedPercent';
import css from './Results.module.scss';

const topTitle = `המאצ' שלך הוא:`;
const tieTopTitle = 'תיקו!';
const resultItemTitle = `party_name בראשות party_leader_name`;
const colTitleMatch = `יש לכם מאצ' מוחלט בנושאים:`;
const colTitleNoMatch = `יש לכם אנמאצ' מוחלט בנושאים:`;
const footerText = '*המידע המוצג נועד להשוואה בלבד ואינו מהווה המלצה או הכוונה להצבעה למפלגה כלשהי';
const link = 'https://www.mako.co.il/news-israel-elections/2026/Article-97e55261416f0a1026.htm';

function isDocumentReload(): boolean {
  if (typeof performance === 'undefined') {
    return false;
  }

  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

  return entry?.type === 'reload';
}

function toEventText(value: string | number | string[] | null | undefined): string {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (value == null) {
    return '';
  }

  return String(value);
}

export default function Results() {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const dispatch = useDispatch<AppDispatch>();
  const [hidePreloader, setHidePreloader] = useState(false);
  const results = useAppSelector(selectResults);
  const parties = useAppSelector(selectParties);
  const isLoadingResults = useAppSelector(selectLoadingResults);
  const [isMobile] = useResponsive(['MOBILE', 'TABLET_P']);
  const didRequestResults = useRef(false);

  const MIN_PRELOADER_TIME = 2000;
  const [isMinTimePassed, setIsMinTimePassed] = useState(false);
  const [lowestPartyByScore, setLowestPartyByScore] = useState<(PartyResult & { partyKey: string }) | null>(null);
  const didTrackArrival = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMinTimePassed(true);
    }, MIN_PRELOADER_TIME);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // useEffect, not useLayoutEffect: HistoryRouter subscribes in its own layout
    // effect, which runs after this component. A layout redirect updates the URL
    // before that subscription exists, so the results view stays mounted.
    if (!clientSession.hasValidAnswers()) {
      navigate(ROOT, { replace: true });
      return;
    }

    const savedResults = clientSession.getResults();

    if (isQuizResults(savedResults)) {
      dispatch(restoreResults(savedResults));
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!(isMinTimePassed && isQuizResults(results) && isQuizResults(clientSession.getResults()))) {
      return;
    }

    setHidePreloader(true);

    const partyAResult = results.parties?.partyA;
    const partyBResult = results.parties?.partyB;

    if (!partyAResult || !partyBResult) {
      return;
    }

    const arrivalAlreadySent = didTrackArrival.current || clientSession.hasTrackedResultsArrival();

    if (arrivalAlreadySent) {
      if (!didTrackArrival.current) {
        didTrackArrival.current = true;
        setLowestPartyByScore(findLowestScoreParty(results.parties));
      }

      return;
    }

    if (isEmpty(parties)) {
      return;
    }

    didTrackArrival.current = true;
    clientSession.markResultsArrivalTracked();

    analytics.gtag.event('הגעה לעמוד סיום', {
      Party_Name_1: partyAResult.name,
      Score_Party_Name_1: toEventText(partyAResult.score),
      Match_Party_Name_1: toEventText(partyAResult.fullMatchExplanations),
      UnMatch_Party_Name_1: toEventText(partyAResult.noMatchExplanations),
      Party_Name_2: partyBResult.name,
      Score_Party_Name_2: toEventText(partyBResult.score),
      Match_Party_Name_2: toEventText(partyBResult.fullMatchExplanations),
      UnMatch_Party_Name_2: toEventText(partyBResult.noMatchExplanations),
    });

    const lowestParty = findLowestScoreParty(results.parties);

    setLowestPartyByScore(lowestParty);
  }, [isMinTimePassed, results, parties]);

  useEffect(() => {
    if (!clientSession.hasValidAnswers() || clientSession.getResults()) {
      return;
    }

    // A stored result is restored above, including on refresh. In-app Back/Forward must
    // not submit again. A reload with no stored result can still fetch once.
    if (navigationType === 'POP' && !isDocumentReload()) {
      if (!isLoadingResults) {
        navigate(ROOT, { replace: true });
      }

      return;
    }

    if (isLoadingResults) {
      return;
    }

    if (didRequestResults.current) {
      navigate(ROOT, { replace: true });
      return;
    }

    didRequestResults.current = true;
    void dispatch(sendResults());
  }, [dispatch, isLoadingResults, navigate, navigationType]);

  const onPlayAgainClick = () => {
    analytics.gtag.event('שחק שוב');
    navigate(ROOT);
  };

  const onAllPromisesClick = () => {
    analytics.gtag.event('לכל ההבטחות');
  };

  const findLowestScoreParty = (parties: Record<string, PartyResult>): (PartyResult & { partyKey: string }) | null => {
    return Object.entries(parties).reduce<(PartyResult & { partyKey: string }) | null>((lowest, [partyKey, party]) => {
      if (!lowest || party.score < lowest.score) {
        return {
          ...party,
          partyKey,
        };
      }

      return lowest;
    }, null);
  };

  return (
    <>
      {!hidePreloader && <Preloader visible={!hidePreloader} />}
      {hidePreloader && (
        <div className={css.results}>
          <div className={css.top}>
            <h1 className={css.title}>{results.isTie ? tieTopTitle : topTitle}</h1>
            <div className={cn(css.card, results.isTie && css.twoCards)}>
              {results.isTie ? (
                <>
                  <Card data={results.parties.partyA} size="s" isClickable={false} className={css.cardBlock} />
                  <Card data={results.parties.partyB} size="s" isClickable={false} className={css.cardBlock} />
                </>
              ) : (
                <Card data={results.winner} size="m" isClickable={false} />
              )}
            </div>
            <Socials inContent className={css.socials} />
            <Icon type="results-star" className={css.starAnimation} />
          </div>
          <div className={cn(css.list, lowestPartyByScore?.partyKey === 'partyA' && css.partyBlueOnBottom)}>
            <div className={cn(css.listItem, css.blue)}>
              <AnimatedPercent
                value={
                  lowestPartyByScore?.partyKey === 'partyA'
                    ? Math.floor(results.parties.partyA.score)
                    : results.parties.partyA.score
                }
              />
              <h4 className={css.partyTitle}>
                {resultItemTitle
                  .replace('party_name', results.parties.partyA.name)
                  .replace('party_leader_name', results.parties.partyA.leaderName)}
              </h4>
              <ProgressLine
                percentNumber={
                  lowestPartyByScore?.partyKey === 'partyA'
                    ? Math.floor(results.parties.partyA.score)
                    : results.parties.partyA.score
                }
                color="blue"
              />
              <div className={css.desc}>
                <div className={css.col}>
                  <h6 className={css.colTitle}>{colTitleMatch}</h6>
                  {isEmpty(results.parties.partyA.fullMatchExplanations) ? (
                    <Icon type="no-results" />
                  ) : (
                    <p className={css.colText}>{toEventText(results.parties.partyA.fullMatchExplanations)}</p>
                  )}
                </div>
                <div className={css.col}>
                  <h6 className={css.colTitle}>{colTitleNoMatch}</h6>
                  {isEmpty(results.parties.partyA.noMatchExplanations) ? (
                    <Icon type="no-results" />
                  ) : (
                    <p className={css.colText}>{toEventText(results.parties.partyA.noMatchExplanations)}</p>
                  )}
                </div>
              </div>
            </div>
            <Icon type="border" className={css.dashedBorder} />
            <div className={cn(css.listItem, css.red)}>
              <AnimatedPercent
                value={
                  lowestPartyByScore?.partyKey === 'partyA'
                    ? results.parties.partyB.score
                    : Math.floor(results.parties.partyB.score)
                }
              />
              <h4 className={css.partyTitle}>
                {resultItemTitle
                  .replace('party_name', results.parties.partyB.name)
                  .replace('party_leader_name', results.parties.partyB.leaderName)}
              </h4>
              <ProgressLine
                percentNumber={
                  lowestPartyByScore?.partyKey === 'partyA'
                    ? results.parties.partyB.score
                    : Math.floor(results.parties.partyB.score)
                }
                color="red"
              />
              <div className={css.desc}>
                <div className={css.col}>
                  <h6 className={css.colTitle}>{colTitleMatch}</h6>
                  {isEmpty(results.parties.partyB.fullMatchExplanations) ? (
                    <Icon type="no-results" />
                  ) : (
                    <p className={css.colText}>{toEventText(results.parties.partyB.fullMatchExplanations)}</p>
                  )}
                </div>
                <div className={css.col}>
                  <h6 className={css.colTitle}>{colTitleNoMatch}</h6>
                  {isEmpty(results.parties.partyB.noMatchExplanations) ? (
                    <Icon type="no-results" />
                  ) : (
                    <p className={css.colText}>{toEventText(results.parties.partyB.noMatchExplanations)}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {isMobile && <p className={css.footerText}>{footerText}</p>}
          <div className={css.footer}>
            <div className={css.buttonsGroup}>
              <a href={link} target="_blank" className={css.btn} onClick={onAllPromisesClick}>
                לכל העמדות
              </a>
              <button type="button" className={cn(css.btn, css.playAgain)} onClick={onPlayAgainClick}>
                שחק שוב
              </button>
            </div>
            {!isMobile && <p className={css.footerText}>{footerText}</p>}
          </div>
        </div>
      )}
      <Cooladata pageId="Results" />
      <StorycardsCredits />
    </>
  );
}
